import React, { useState, useEffect, useCallback } from 'react';
import { Building2, Upload, Check, RefreshCw, AlertCircle, ShieldOff, Image as ImageIcon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { NavBar } from './NavBar';

interface ProfileData {
  company_name: string;
  address: string;
  phone: string;
  website: string;
  has_logo: boolean;
  logo_url: string | null;
}

const EMPTY: ProfileData = { company_name: '', address: '', phone: '', website: '', has_logo: false, logo_url: null };

export function CompanyProfile() {
  const { getToken, getGroups } = useAuth();
  const API_URL = process.env.REACT_APP_API_URL ?? '';

  const [checkingAccess, setCheckingAccess] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  const [profile, setProfile] = useState<ProfileData>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg] = useState<{ type: 'ok' | 'error'; text: string } | null>(null);

  const callApi = useCallback(async (action: string, extra: Record<string, any> = {}) => {
    const token = await getToken();
    const res = await fetch(`${API_URL}/company-profile`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ action, ...extra }),
    });
    const result = await res.json();
    if (!res.ok) throw new Error(result.error ?? `HTTP ${res.status}`);
    return result;
  }, [API_URL, getToken]);

  useEffect(() => {
    getGroups()
      .then(groups => setIsAdmin(groups.includes('company-admin')))
      .catch(() => setIsAdmin(false))
      .finally(() => setCheckingAccess(false));
  }, [getGroups]);

  const loadProfile = useCallback(async () => {
    setLoading(true);
    try {
      const result = await callApi('get_profile');
      setProfile(result);
    } catch (e: any) {
      setMsg({ type: 'error', text: e.message ?? 'Failed to load company profile' });
    } finally {
      setLoading(false);
    }
  }, [callApi]);

  useEffect(() => {
    if (isAdmin) loadProfile();
  }, [isAdmin, loadProfile]);

  const saveFields = async () => {
    setSaving(true);
    setMsg(null);
    try {
      const result = await callApi('update_profile', {
        fields: {
          company_name: profile.company_name,
          address: profile.address,
          phone: profile.phone,
          website: profile.website,
        },
      });
      setProfile(result);
      setMsg({ type: 'ok', text: 'Company profile saved.' });
    } catch (e: any) {
      setMsg({ type: 'error', text: e.message ?? 'Failed to save' });
    } finally {
      setSaving(false);
    }
  };

  const uploadLogo = async (file: File) => {
    setUploading(true);
    setMsg(null);
    try {
      const { upload_url } = await callApi('get_logo_upload_url', { content_type: file.type });
      const putRes = await fetch(upload_url, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      });
      if (!putRes.ok) throw new Error('Upload to storage failed');
      setMsg({ type: 'ok', text: 'Logo uploaded.' });
      await loadProfile();
    } catch (e: any) {
      setMsg({ type: 'error', text: e.message ?? 'Failed to upload logo' });
    } finally {
      setUploading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!['image/png', 'image/jpeg', 'image/svg+xml'].includes(file.type)) {
      setMsg({ type: 'error', text: 'Please choose a PNG, JPEG, or SVG file.' });
      return;
    }
    uploadLogo(file);
    e.target.value = '';
  };

  if (checkingAccess) {
    return (
      <div className="min-h-screen bg-gray-50">
        <NavBar />
        <main className="max-w-2xl mx-auto px-4 py-20 text-center text-gray-400">
          <RefreshCw className="w-8 h-8 mx-auto mb-3 animate-spin opacity-50" />
        </main>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-50">
        <NavBar />
        <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <ShieldOff className="w-14 h-14 mx-auto mb-4 text-gray-300" />
          <h1 className="text-lg font-bold text-gray-900">Access Restricted</h1>
          <p className="text-sm text-gray-500 mt-2">
            You don't have permission to manage the company profile. Contact your company administrator if you believe this is an error.
          </p>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <NavBar />

      <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-5">

        <div className="flex items-center space-x-3">
          <div className="flex items-center justify-center w-10 h-10 bg-slate-700 rounded-lg">
            <Building2 className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Company Profile</h1>
            <p className="text-sm text-gray-500">Your logo and company details, used on branded reports and exports</p>
          </div>
        </div>

        {loading ? (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm text-center py-16 text-gray-400">
            <RefreshCw className="w-8 h-8 mx-auto mb-3 animate-spin opacity-50" />
          </div>
        ) : (
          <>
            {/* Logo */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 space-y-4">
              <h2 className="text-sm font-bold text-gray-900">Logo</h2>
              <div className="flex items-center space-x-5">
                <div className="w-32 h-32 border border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50 overflow-hidden flex-shrink-0">
                  {profile.logo_url ? (
                    <img src={profile.logo_url} alt="Company logo" className="max-w-full max-h-full object-contain" />
                  ) : (
                    <ImageIcon className="w-8 h-8 text-gray-300" />
                  )}
                </div>
                <div>
                  <label className="inline-flex items-center space-x-2 px-4 py-2 bg-slate-700 text-white text-sm font-medium rounded-lg hover:bg-slate-800 cursor-pointer transition-colors">
                    {uploading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    <span>{uploading ? 'Uploading…' : profile.has_logo ? 'Replace Logo' : 'Upload Logo'}</span>
                    <input type="file" accept="image/png,image/jpeg,image/svg+xml" className="hidden" onChange={handleFileChange} disabled={uploading} />
                  </label>
                  <p className="text-xs text-gray-400 mt-2">PNG, JPEG, or SVG. Used on branded PDF and Excel report exports.</p>
                </div>
              </div>
            </div>

            {/* Company info */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 space-y-4">
              <h2 className="text-sm font-bold text-gray-900">Company Details</h2>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Company Name</label>
                <input
                  type="text" value={profile.company_name}
                  onChange={e => setProfile(p => ({ ...p, company_name: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Address</label>
                <input
                  type="text" value={profile.address}
                  onChange={e => setProfile(p => ({ ...p, address: e.target.value }))}
                  placeholder="Optional"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Phone</label>
                  <input
                    type="text" value={profile.phone}
                    onChange={e => setProfile(p => ({ ...p, phone: e.target.value }))}
                    placeholder="Optional"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Website</label>
                  <input
                    type="text" value={profile.website}
                    onChange={e => setProfile(p => ({ ...p, website: e.target.value }))}
                    placeholder="Optional"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500"
                  />
                </div>
              </div>

              {msg && (
                <div className={`flex items-start space-x-2 rounded-lg px-3 py-2 ${msg.type === 'ok' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                  {msg.type === 'ok'
                    ? <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                    : <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />}
                  <p className={`text-xs ${msg.type === 'ok' ? 'text-green-700' : 'text-red-700'}`}>{msg.text}</p>
                </div>
              )}

              <button
                onClick={saveFields}
                disabled={saving || !profile.company_name.trim()}
                className="flex items-center space-x-2 px-4 py-2 bg-slate-700 text-white text-sm font-medium rounded-lg hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                <span>{saving ? 'Saving…' : 'Save Details'}</span>
              </button>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
